import type {ConfiguratorVariant} from "#pricing/types/pricing-types"
import {configuratorVariantSchema} from "#pricing/types/pricing-types"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type VariantSelectedConditionParameters = {
    groups: ParameterValue<ConfiguratorVariant, PricingContext>[] //usually ConfigurationGroupNodes; several only for mutually exclusive groups (funnel)
    variantIds: string[]
}

const variantSelectedConditionDefinition = {
    nodeClass: "VariantSelectedCondition",
    validation: {
        parameters: z.object({
            groups: z.array(parameterValue(configuratorVariantSchema)),
            variantIds: z.array(z.string()),
        }),
        returns: z.boolean(),
    },
} satisfies GraphNodeDefinition<boolean, VariantSelectedConditionParameters>

/**
 * True if the current variant of the active group is one of `variantIds`. Several groups can be fed in if they are mutually
 * exclusive (e.g. the finish groups of different taps), at most one of them may be active. See Conditions.md.
 */
@registerPricingGraphNode
export class VariantSelectedCondition extends PricingNode<boolean, VariantSelectedConditionParameters> {
    public override get definition() {
        return variantSelectedConditionDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        if (this.parameters.variantIds.length === 0) return false
        const variants = await Promise.all(this.parameters.groups.map((group) => evaluator.get(group)))
        //groups that are not part of the current configuration have an empty variant id
        const activeVariants = variants.filter((variant) => variant.variantId !== "")
        //returning a result would price the same thing twice, so fail loudly instead
        if (activeVariants.length > 1)
            throw new Error(`VariantSelectedCondition: groups must be mutually exclusive, active: ${activeVariants.map((v) => v.groupId).join(", ")}`)
        return activeVariants.some((variant) => this.parameters.variantIds.includes(variant.variantId))
    }
}
